// Discussion: spoiler-gated — only players who finished the edition can read or post.
import { useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router'
import type { Comment } from '@wbwl/contract'
import { Icon } from '../components/Icon.tsx'
import { Avatar, ErrorNote, Loading, NotFound, useToast } from '../components/shell.tsx'
import { ApiFailure, api } from '../lib/api.ts'
import { useMe, useThread } from '../lib/data.ts'
import { invalidate } from '../lib/store.ts'

export default function DiscussionPage() {
  const n = Number(useParams().number)
  if (!Number.isInteger(n) || n < 0) return <NotFound />
  return <Discussion n={n} />
}

function Discussion({ n }: { n: number }) {
  const thread = useThread(n)
  const me = useMe().data
  const [body, setBody] = useState('')
  const [replyTo, setReplyTo] = useState<Comment | null>(null)
  const toast = useToast()

  async function post(e: FormEvent) {
    e.preventDefault()
    const text = body.trim()
    if (!text) return
    try {
      await api.post(`/editions/${n}/comments`, { body: text, parentId: replyTo?.id ?? null })
      setBody(''); setReplyTo(null)
      invalidate(`thread:${n}`)
    } catch (err) {
      toast(err instanceof ApiFailure && err.body.error === 'rate_limited' ? 'You’re posting fast — try again in a minute' : 'Couldn’t post that')
    }
  }
  async function remove(c: Comment) {
    try { await api.del(`/comments/${c.id}`); invalidate(`thread:${n}`) } catch { toast('Couldn’t delete that') }
  }

  const t = thread.data
  // Replies sit under their top-level comment. One whose parent isn't shown as a top-level comment
  // (deleted, or itself a reply) is shown on its own, so every counted comment appears once.
  const topIds = new Set(t ? t.comments.filter((c) => !c.parentId).map((c) => c.id) : [])
  const top = t ? t.comments.filter((c) => !c.parentId || !topIds.has(c.parentId)) : []
  const replies = (id: string) => (t && topIds.has(id) ? t.comments.filter((c) => c.parentId === id) : [])

  return (
    <div className="page discussion">
      <div className="topbar">
        <Link className="ibtn" to={`/results/${n}`} aria-label="Back to results"><Icon name="back" /></Link>
        <div className="title"><b>No. {n} discussion</b><small>{t ? `${t.count} COMMENT${t.count === 1 ? '' : 'S'} · SPOILERS INSIDE` : 'SPOILERS INSIDE'}</small></div>
      </div>
      {thread.error ? <ErrorNote error={thread.error} retry={() => thread.reload()} /> : !t ? <Loading label="Loading the conversation…" /> : t.locked ? (
        <div className="card locked">
          <Icon name="eyeoff" size={22} />
          <div><b>Finish No. {n} to join in.</b><p className="muted small">The discussion is full of words and strategy, so it opens once your play is done.</p></div>
          <Link className="btn ink sm" to={`/play/${n}`}>Play No. {n}</Link>
        </div>
      ) : (
        <>
          <div className="gate-note"><Icon name="eyeoff" size={18} /><span className="muted small">Only players who finished No. {n} can read and post here.</span></div>
          {top.length === 0 && <p className="muted empty-note">No comments yet. Say how it went.</p>}
          {top.map((c) => (
            <div key={c.id}>
              <CommentRow c={c} onReply={() => setReplyTo(c)} onDelete={() => remove(c)} />
              {replies(c.id).map((r) => <CommentRow key={r.id} c={r} reply onDelete={() => remove(r)} />)}
            </div>
          ))}
          <div style={{ height: 96 }} />
          <form className="composer" onSubmit={post}>
            {replyTo && <div className="replying faint small">Replying to {replyTo.author.displayName} <button type="button" className="btn plain sm" onClick={() => setReplyTo(null)}>Cancel</button></div>}
            <div className="composer-row">
              <Avatar id={me?.id} name={me?.displayName} src={me?.avatar} size={32} />
              <input value={body} onChange={(e) => setBody(e.target.value)} maxLength={1000} placeholder={`Say something about No. ${n}…`} aria-label="Comment" />
              <button className="btn ink sm" type="submit" disabled={!body.trim()}>Post</button>
            </div>
          </form>
        </>
      )}
    </div>
  )
}

export function CommentRow({ c, reply, onReply, onDelete }: { c: Comment; reply?: boolean; onReply?(): void; onDelete(): void }) {
  const ago = timeAgo(c.createdAt)
  return (
    <div className={`cm${reply ? ' reply' : ''}`}>
      <Avatar id={c.author.id} name={c.author.displayName} src={c.author.avatar} />
      <div>
        <div className="hd"><b>{c.author.displayName}</b>{c.authorRankName && <span className="r">{c.authorRankName} · {c.authorTotal}</span>}<span className="tm">{ago}</span></div>
        <p>{c.body}</p>
        <div className="ft">
          {onReply && <button type="button" onClick={onReply}><Icon name="reply" />Reply</button>}
          {c.mine && <button type="button" onClick={onDelete}>Delete</button>}
        </div>
      </div>
    </div>
  )
}

export function timeAgo(iso: string): string {
  const s = (Date.now() - Date.parse(iso)) / 1000
  if (s < 60) return 'now'
  if (s < 3600) return `${Math.floor(s / 60)}m`
  if (s < 86400) return `${Math.floor(s / 3600)}h`
  return `${Math.floor(s / 86400)}d`
}
